import { useState } from "react";
import {
  LogOut,
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Package,
  FolderTree,
  Store,
  Mail,
  MessageCircle,
  Image,
  Layers,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import AdminDashboard from "../components/admin/AdminDashboard";
import AdminProducts from "../components/admin/AdminProducts";
import AdminCategories from "../components/admin/AdminCategories";
import AdminShopify from "../components/admin/AdminShopify";
import { AdminArtworkUploader, AdminBulkReupload, AdminUploads } from "../components/admin/AdminMedia";
import AdminOrders from "../components/admin/AdminOrders";
import AdminEmails from "../components/admin/AdminEmails";
import AdminWhatsApp from "../components/admin/AdminWhatsApp";
import AdminAnalytics from "../components/admin/AdminAnalytics";
import AdminTemplatePacks from "../components/admin/AdminTemplatePacks";
import MockupLibrary from "../components/admin/MockupLibrary";

/* ── Main Admin Page ── */

export default function Admin() {
  const { logout } = useAdminAuth();
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleLogout = async () => {
    await logout();
    window.location.href = "/admin/login";
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-panel">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#060606]">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="AM Decor" className="h-8 w-auto opacity-70" />
            <div>
              <h1 className="text-sm font-bold text-white">Admin Panel</h1>
              <p className="text-[10px] text-white/30">AM Decor Signs &amp; Prints</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-[10px] text-white/30 hover:text-white/60 uppercase tracking-widest transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <TabsList className="bg-transparent border border-white/[0.06] h-auto p-0 rounded-none inline-flex">
              <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <BarChart3 className="w-3 h-3 mr-1.5 inline" />Dashboard
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="admin-tab-products" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <Package className="w-3 h-3 mr-1.5 inline" />Products
              </TabsTrigger>
              <TabsTrigger value="categories" data-testid="admin-tab-categories" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <FolderTree className="w-3 h-3 mr-1.5 inline" />Categories
              </TabsTrigger>
              <TabsTrigger value="orders" data-testid="admin-tab-orders" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <ShoppingCart className="w-3 h-3 mr-1.5 inline" />Orders
              </TabsTrigger>
              <TabsTrigger value="shopify" data-testid="admin-tab-shopify" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <Store className="w-3 h-3 mr-1.5 inline" />Shopify
              </TabsTrigger>
              <TabsTrigger value="media" data-testid="admin-tab-media" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <Image className="w-3 h-3 mr-1.5 inline" />Media
              </TabsTrigger>
              <TabsTrigger value="emails" data-testid="admin-tab-emails" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <Mail className="w-3 h-3 mr-1.5 inline" />Emails
              </TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="admin-tab-whatsapp" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <MessageCircle className="w-3 h-3 mr-1.5 inline" />WhatsApp
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <TrendingUp className="w-3 h-3 mr-1.5 inline" />Analytics
              </TabsTrigger>
              <TabsTrigger value="templates" data-testid="admin-tab-templates" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/40 px-3 md:px-5 text-xs md:text-sm whitespace-nowrap">
                <Layers className="w-3 h-3 mr-1.5 inline" />Templates
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="mt-6">
            <TabsContent value="dashboard">
              <AdminDashboard />
            </TabsContent>
            <TabsContent value="products">
              <AdminProducts />
            </TabsContent>
            <TabsContent value="categories">
              <AdminCategories />
            </TabsContent>
            <TabsContent value="orders">
              <AdminOrders />
            </TabsContent>
            <TabsContent value="shopify">
              <AdminShopify />
            </TabsContent>
            <TabsContent value="media">
              <div className="space-y-6" data-testid="admin-media-tab-content">
                <AdminArtworkUploader />
                <AdminBulkReupload />
                <AdminUploads />
              </div>
            </TabsContent>
            <TabsContent value="emails">
              <AdminEmails />
            </TabsContent>
            <TabsContent value="whatsapp">
              <AdminWhatsApp />
            </TabsContent>
            <TabsContent value="analytics">
              <AdminAnalytics />
            </TabsContent>
            <TabsContent value="templates">
              <div className="space-y-6">
                <AdminTemplatePacks />
                <MockupLibrary />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
